import {
  useActionData,
  useMatches,
  useParams,
  useRouteLoaderData,
} from 'react-router-dom';
import {
  BaseTask,
  Task,
  TaskQuestion,
  TaskRef,
  TaskRequirement,
  TaskStatus,
  TaskType,
} from './types';
import { LOCATION_CODE_TO_NAME } from '../../../utils/locations';
import { TaskActionData } from '../tasks/types';
import { AutomationTypeEnum } from '../../../types';

export const useTasks = () => {
  return (useRouteLoaderData('resolver-tasks') || []) as TaskRef[];
};

export const useTask = () => {
  const task = useRouteLoaderData('resolver-task') as Task;

  if (!task) {
    throw new Error(
      'The useTask hook must be used within a Task Resolver Task route.'
    );
  }

  return task;
};

export const useTaskQuestion = () => {
  return useTask() as TaskQuestion;
};

export const useTaskRequirement = () => {
  return useTask() as TaskRequirement;
};

const isTaskTodo = ({ status }: BaseTask) => status === TaskStatus.Todo;

export const useTodoTasks = () => {
  const tasks = useTasks();

  return tasks.filter(isTaskTodo);
};

export const useNextTask = (currentTaskId?: Task['id']) => {
  let tasks = useTasks();

  if (currentTaskId) {
    const currentIndex = tasks.findIndex(({ id }) => id === currentTaskId) + 1;

    tasks = tasks.slice(currentIndex).concat(tasks.slice(0, currentIndex));
  }

  return tasks.find(isTaskTodo);
};

export const useResolverUrl = () => {
  const matches = useMatches();

  return (
    matches.find(({ id }) => id === 'resolver-tasks')?.pathname ??
    '/tasks/resolver'
  );
};

export const useTaskUrl = () => {
  const matches = useMatches();
  const url = matches.find(({ id }) => id === 'resolver-task');

  if (!url) {
    throw new Error(
      'The useTaskUrl hook must be used within a Task Resolver Task route.'
    );
  }

  return url.pathname;
};

export const useTaskLocation = () => {
  const { locationId } = useParams();
  const { source } = useTask();

  if (locationId) {
    return {
      code: locationId,
      name: LOCATION_CODE_TO_NAME[locationId.toUpperCase()],
    };
  } else if (source.type === TaskType.Requirement) {
    return {
      code: source.location.code,
      name: source.location.name,
    };
  } else if (source.locations.length === 1) {
    return {
      code: source.locations[0].code,
      name: source.locations[0].name,
    };
  }

  return null;
};

export const useTaskActionData = () => {
  return useActionData() as TaskActionData;
};

export const useNewTasks = (hideNonResolverTasks = false) => {
  const allTasks = useTasks();
  const taskActionData = useTaskActionData();

  if (taskActionData) {
    if (hideNonResolverTasks) {
      return taskActionData.newTasks.filter((newTask) => {
        return allTasks.find((task) => task.id === newTask.id);
      });
    }

    return taskActionData.newTasks;
  }

  return [];
};

export const useIsNoticesAndPostersTask = () => {
  const { source } = useTask();

  return (
    source.type === TaskType.Requirement &&
    source.tags.includes(AutomationTypeEnum.NoticeBoard)
  );
};
